/**
 * GenerationProgress — a self-contained live indicator for long-running
 * background enrichment (plan / evidence reconciliation / move tailoring).
 * Shows an elapsed timer, a clear time expectation and an indeterminate bar so
 * the user always knows it is working and roughly how long it takes. The job
 * runs server-side, so the user can switch tabs or close the panel safely.
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function GenerationProgress({ active, label = "Generating", typical = "~2–3 min", testid = "generation-progress" }) {
  const [elapsed, setElapsed] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (active) {
      setElapsed(0);
      timer.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    return () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  }, [active]);

  if (!active) return null;

  return (
    <div className="mb-3 border border-[#7737F6]/30 bg-[#F7F4FF] px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[12.5px] font-medium text-[#5D28C7]">
          <Loader2 size={14} className="animate-spin"/> {label}…
        </span>
        <span className="text-[12.5px] font-semibold tabular-nums text-[#5D28C7]" data-testid={`${testid}-elapsed`}>
          {fmt(elapsed)}
        </span>
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden bg-[#7737F6]/15">
        <div className="h-full w-1/3 bg-[#7737F6] ek-indeterminate"/>
      </div>
      <p className="mt-1.5 text-[10.5px] leading-relaxed text-zinc-500">
        Typically {typical}. This runs in the background — you can switch tabs or close this panel and it will keep going.
      </p>
    </div>
  );
}

export default GenerationProgress;
